"use client";

import {
  IconChevronLeft,
  IconChevronRight,
  IconPhoto,
  IconX,
  IconZoomIn,
} from "@tabler/icons-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { Chantier } from "@/lib/db/schema";

type CategoryGroup = {
  id: string;
  label: string;
  items: readonly Chantier[];
};

type Props = {
  groups: readonly CategoryGroup[];
};

// Position de la photo ouverte : la catégorie (index dans `groups`) + l'index du
// chantier dans cette catégorie. La lightbox ne navigue qu'au sein de la catégorie.
type OpenState = { cat: number; item: number };

export function RealisationsGallery({ groups }: Props) {
  const [open, setOpen] = useState<OpenState | null>(null);
  const isOpen = open !== null;

  const close = useCallback(() => setOpen(null), []);

  // Deep-link : /realisations#<id de catégorie> (ex. #entretien depuis la carte
  // Entretien de /services) ouvre directement la lightbox de cette catégorie.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const cat = groups.findIndex((g) => g.id === id && g.items.length > 0);
    if (cat !== -1) setOpen({ cat, item: 0 });
  }, [groups]);
  const prev = useCallback(() => {
    setOpen((o) => {
      if (o === null) return null;
      const len = groups[o.cat].items.length;
      return { ...o, item: (o.item - 1 + len) % len };
    });
  }, [groups]);
  const next = useCallback(() => {
    setOpen((o) => {
      if (o === null) return null;
      const len = groups[o.cat].items.length;
      return { ...o, item: (o.item + 1) % len };
    });
  }, [groups]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, close, prev, next]);

  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;
    const body = document.body;
    const docEl = document.documentElement;
    const scrollbarWidth = window.innerWidth - docEl.clientWidth;

    const previous = {
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyPaddingRight: body.style.paddingRight,
      htmlOverflow: docEl.style.overflow,
    };

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    docEl.style.overflow = "hidden";

    return () => {
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      body.style.paddingRight = previous.bodyPaddingRight;
      docEl.style.overflow = previous.htmlOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  return (
    <>
      <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-3">
        {groups.map((group, catIndex) => {
          const count = group.items.length;
          const cover = group.items[0];

          // Catégorie sans chantier publié : cadre informatif, non cliquable.
          if (!cover) {
            return (
              <div
                key={group.id}
                className="relative aspect-4/5 overflow-hidden rounded-sm bg-border flex flex-col items-center justify-center gap-3 text-center px-6"
              >
                <IconPhoto size={28} stroke={1.25} className="text-text-muted" aria-hidden />
                <span
                  className="text-lg md:text-xl text-text-primary tracking-tight"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {group.label}
                </span>
                <span className="text-xs uppercase tracking-[0.2em] text-text-muted">Bientôt</span>
              </div>
            );
          }

          return (
            <button
              key={group.id}
              type="button"
              onClick={() => setOpen({ cat: catIndex, item: 0 })}
              aria-label={`${group.label} — voir ${count} ${count > 1 ? "réalisations" : "réalisation"}`}
              className="group relative aspect-4/5 overflow-hidden rounded-sm bg-border cursor-zoom-in text-left focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-4"
            >
              {/* Couverture décorative : le bouton porte déjà aria-label.
                  Les alt descriptifs (imageAlt) restent sur les images de la lightbox. */}
              <Image
                src={cover.imageUrl}
                alt=""
                fill
                sizes="(min-width: 768px) 31vw, 92vw"
                className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
              />

              <div
                aria-hidden
                className="absolute inset-0 bg-linear-to-t from-(--background)/90 via-(--background)/15 to-transparent opacity-75 group-hover:opacity-100 transition-opacity duration-500"
              />

              <span
                aria-hidden
                className="absolute top-4 left-4 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase text-text-primary bg-(--background)/50 backdrop-blur-sm border border-(--text-primary)/15 rounded-sm"
              >
                {count} {count > 1 ? "réalisations" : "réalisation"}
              </span>

              <span
                aria-hidden
                className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-(--background)/60 backdrop-blur-sm border border-(--text-primary)/15 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300"
              >
                <IconZoomIn size={15} stroke={1.75} className="text-text-primary" />
              </span>

              <span className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <span
                  className="block text-2xl md:text-3xl text-text-primary tracking-tight leading-snug translate-y-2 group-hover:translate-y-0 transition-transform duration-500 ease-out"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {group.label}
                </span>
              </span>

              <span
                aria-hidden
                className="absolute bottom-0 left-0 h-[2px] w-10 bg-primary-light group-hover:w-full transition-all duration-700 ease-out"
              />
            </button>
          );
        })}
      </div>

      {open !== null && typeof document !== "undefined"
        ? createPortal(
            <Lightbox
              categoryLabel={groups[open.cat].label}
              items={groups[open.cat].items}
              index={open.item}
              onClose={close}
              onPrev={prev}
              onNext={next}
            />,
            document.body,
          )
        : null}
    </>
  );
}

type LightboxProps = {
  categoryLabel: string;
  items: readonly Chantier[];
  index: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
};

function Lightbox({ categoryLabel, items, index, onClose, onPrev, onNext }: LightboxProps) {
  const r = items[index];
  const hasMultiple = items.length > 1;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape est gere par un keydown listener au niveau window dans le parent
    <div
      role="dialog"
      aria-modal="true"
      aria-label={categoryLabel}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="on-dark-image fixed inset-0 z-100 flex items-center justify-center p-4 md:p-10"
      style={{
        backgroundColor: "rgba(10, 10, 10, 0.94)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        animation: "fadeIn 220ms ease-out",
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="absolute top-4 right-4 md:top-6 md:right-6 flex items-center justify-center w-11 h-11 rounded-full border border-(--text-primary)/20 bg-(--background)/60 text-text-primary hover:bg-(--text-primary)/10 hover:border-(--text-primary)/40 transition-colors"
      >
        <IconX size={18} stroke={1.75} />
      </button>

      {hasMultiple ? (
        <>
          <button
            type="button"
            onClick={onPrev}
            aria-label="Réalisation précédente"
            className="hidden md:flex absolute left-4 md:left-6 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 rounded-full border border-(--text-primary)/20 bg-(--background)/60 text-text-primary hover:bg-(--text-primary)/10 hover:border-(--text-primary)/40 transition-colors"
          >
            <IconChevronLeft size={22} stroke={1.75} />
          </button>

          <button
            type="button"
            onClick={onNext}
            aria-label="Réalisation suivante"
            className="hidden md:flex absolute right-4 md:right-6 top-1/2 -translate-y-1/2 items-center justify-center w-12 h-12 rounded-full border border-(--text-primary)/20 bg-(--background)/60 text-text-primary hover:bg-(--text-primary)/10 hover:border-(--text-primary)/40 transition-colors"
          >
            <IconChevronRight size={22} stroke={1.75} />
          </button>
        </>
      ) : null}

      <figure className="relative w-full max-w-5xl mx-auto flex flex-col gap-4 md:gap-6 max-h-full">
        <div className="relative aspect-4/3 md:aspect-16/10 w-full overflow-hidden rounded-sm bg-border">
          <Image
            key={r.id}
            src={r.imageUrl}
            alt={r.imageAlt}
            fill
            sizes="(min-width: 1024px) 80vw, 95vw"
            priority
            className="object-contain"
          />
        </div>
        <figcaption className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            {/* Classes text-(--…) runtime et non text-text-* (figées au build) :
                on-dark-image remappe les variables en crème sur le backdrop sombre. */}
            <p className="text-[11px] uppercase tracking-[0.22em] text-(--text-muted) mb-1.5">
              {categoryLabel}
              {hasMultiple
                ? ` · ${String(index + 1).padStart(2, "0")} / ${String(items.length).padStart(2, "0")}`
                : ""}
            </p>
            <h3
              className="text-2xl md:text-3xl tracking-tight text-(--text-primary)"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {r.titre}
            </h3>
          </div>

          {hasMultiple ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onPrev}
                aria-label="Précédente"
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-full border border-(--text-primary)/20 text-(--text-primary)"
              >
                <IconChevronLeft size={18} stroke={1.75} />
              </button>
              <button
                type="button"
                onClick={onNext}
                aria-label="Suivante"
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-full border border-(--text-primary)/20 text-(--text-primary)"
              >
                <IconChevronRight size={18} stroke={1.75} />
              </button>
            </div>
          ) : null}
        </figcaption>
      </figure>
    </div>
  );
}
